// Importing necessary libraries and components
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import styles from "assets/jss/material-dashboard-react/components/businessForm";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format, parseISO, subMonths } from "date-fns";
import { Checkbox } from "@material-ui/core";
import { createTheme } from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";
import {
  getApiLogsData,
  setFormDataApiLogs,
  clearFormDataApiLogs,
} from "../../Redux/Actions/merchantInformation";
import {
  getDefaultDateRange,
  getAutoDateRange,
  isDateRangeValid,
  getFormattedDate
} from "Utils/commonUtils";
import { getDateRange } from "Utils/commonUtils";

// Custom theme for Material-UI components
export const customTheme = createTheme({
  palette: {
    primary: {
      main: "#003c6e", // Primary color
      light: "#003c6e",
      dark: "#003c6e",
    },
    secondary: {
      main: "#003c6e", // Secondary color
    },
  },
  overrides: {
    MuiPickersBasePicker: {
      container: {
        width: "300px",
        height: "370px",
      },
      pickerView: {
        width: "280px",
        height: "280px",
      },
    },
    MuiPickersCalendarHeader: {
      switchHeader: {
        fontSize: "0.9rem",
      },
      dayLabel: {
        fontSize: "0.7rem",
      },
    },
    MuiPickersDay: {
      day: {
        fontSize: "0.8rem",
      },
    },
    MuiTypography: {
      h4: {
        fontSize: "1rem",
      },
    },
  },
});

// Custom styles for the component
const useStyles = makeStyles(styles);
const searchButtonStyles = makeStyles(theme => ({
  searchButton: {
    backgroundColor: theme.palette.primary.main, // Button background color
    "&:hover": {
      backgroundColor: theme.palette.primary.main, // Hover effect
      boxShadow:
        "0 14px 26px -12px rgb(153 153 153 / 42%), 0 4px 23px 0px rgb(0 0 0 / 12%), 0 8px 10px -5px rgb(153 153 153 / 20%)",
    },
  },
}));

// Main component for API Logs Search
function ApiLogsSearch(props) {
  const classes = useStyles();
  const searchButtonClasses = searchButtonStyles();
  const [showDateRange, setShowDateRange] = React.useState(true);
  const [showDateRangeError, setShowDateRangeError] = React.useState(false);
  const [date1, setDate1] = React.useState("");
  const [date2, setDate2] = React.useState("");
  const [apiType, setApiType] = React.useState("");
  const [apiTypeValue, setApiTypeValue] = React.useState("");
  const [module, setModule] = React.useState("");
  const [searchStatus, setSearchStatus] = React.useState("");
  const [requestedFor, setRequestedFor] = React.useState(null);
  const [moduleValue, setModuleValue] = React.useState("");
  const menu = JSON.parse(localStorage.getItem("menu"));
  const { startDate, endDate } = getDefaultDateRange("RTP");

  const clearFormData = () => {
    const initialFormData = {
      searchRequestedFor: "",
      searchApiType: "",
      searchApiTypeValue: "",
      searchModule: "",
      searchModuleValue: "",
      searchResponseDate: "",
      searchStartDate: "",
      searchEndDate: "",
      searchStatus: null,
      searchStatusValue: "",
    };
    setRequestedFor("");
    setShowDateRange(false);
    setDate1("");
    setDate2("");
    props.setFormDataApiLogs(initialFormData);
  };  

  const handleClose = () => {
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    const payload = {
      Profile_Code: profileCode,
      pageNo: 1,
      recordPerPage: 5,
      searchStartDate: startDate,
      searchEndDate: endDate,
      dateRange: true
    };
    props.getApiLogsData(payload);
    props.clearFormDataApiLogs();
    clearFormData();
    props.clearChips();
  };

  const handleApiTypeChange = value => {
    let Type_Code = null;

    switch (value) {
      case "RTN API":
        Type_Code = 1;
        break;
      case "Send Fund API":
        Type_Code = 2;
        break;
      default:
        Type_Code = null;
        break;
    }
    setApiType(Type_Code);
    setApiTypeValue(value);
  };

 const handleSearchStartDateChange = (date) => {
   const formattedDate = getFormattedDate(date);
   if (!formattedDate) {
     return;
   }
   setDate1(formattedDate);
 };

const handleSearchEndDateChange = (date) => {
  const formattedDate = getFormattedDate(date);
  if (!formattedDate) {
    return;
  }
  setDate2(formattedDate);
};

  const handleDateCheckboxChange = (event) => {
    const checked = event.target.checked;
    setShowDateRange(checked);
    if (checked) {
      const today = new Date();
      const maxDateRange = getDateRange("RTP", "Months") ?? 2;
      const endDate = format(today, "yyyy-MM-dd");
      const startDate = format(subMonths(today, maxDateRange), "yyyy-MM-dd");

      setDate1(startDate);
      setDate2(endDate);
    } else {
      setDate1("");
      setDate2("");
    }
  };
  const handleModuleChange = value => {
    let Module_Code = null;

    switch (value) {
      case "Invite Page":
        Module_Code = 1;
        break;
      case "Merchant Submit Page":
        Module_Code = 2;
        break;
      case "Make Payment Page":
        Module_Code = 3;
        break;
      case "Merchant SignUp Page":
        Module_Code = 4;
        break;
      default:
        Module_Code = null;
        break;
    }
    setModule(Module_Code);
    setModuleValue(value);
  };

  const onChange = e => {
    const { name, value } = e.target;
    if (name === "searchApiType") {
      handleApiTypeChange(value);
    } else if (name === "searchModule") {
      handleModuleChange(value);
    }
  };
  const handleInputChange = e => {
    const { value } = e.target;

    if (value.length === 0) {
      // Allow clearing the input
      setRequestedFor(value);
      return;
    }

    const firstChar = value[0];

    if (Number.isNaN(Number(firstChar))) {
      // First character is not a number
      if (/^[a-zA-Z]*$/.test(value)) {
        setRequestedFor(value);
      }
    } else if (/^\d*$/.test(value)) {
      // All digits
      setRequestedFor(value);
    }
  };

  const handleStatusChange = e => {
    const { value } = e.target;
    setSearchStatus(value);
  };
  React.useEffect(() => {
    if (props.formData.searchRequestedFor) {
      setRequestedFor(props.formData.searchRequestedFor);
    }
    if (props.formData.searchApiType) {
      setApiType(props.formData.searchApiType);
    }
    if (props.formData.searchApiTypeValue) {
      setApiTypeValue(props.formData.searchApiTypeValue);
    }
    if (props.formData.searchModule) {
      setModule(props.formData.searchModule);
    }
    if (props.formData.searchModuleValue) {
      setModuleValue(props.formData.searchModuleValue);
    }
    if (props.formData.searchStatus) {
      setSearchStatus(props.formData.searchStatus);
    }
    setDate1(
      props.formData.searchStartDate || props.activeSearchData?.searchStartDate || ""
    );
    setDate2(
      props.formData.searchEndDate || props.activeSearchData?.searchEndDate || ""
    );
    const dateRange =
      props.formData.dateRange || props.activeSearchData?.dateRange;
    if (dateRange !== undefined) {
      setShowDateRange(dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.formData]);

  const onFormSubmit = event => {
    event.preventDefault();
    if (showDateRange && date1 && date2) {
      const isValid = isDateRangeValid(date1, date2, "RTP");
      if (!isValid) {
        setShowDateRangeError(true)
        return;
      }
    }
    const nextFormData = {
      ...(props.formData || {}),
      searchRequestedFor: requestedFor || "",
      searchApiType: apiType || "",
      searchApiTypeValue: apiTypeValue || "",
      searchModule: module || "",
      searchModuleValue: moduleValue || "",
      searchStartDate: date1 || null,
      searchEndDate: showDateRange ? date2 : null,
      searchStatus: searchStatus ? searchStatus.trim() : "",
      amountRange: false,
      dateRange: showDateRange,
    };
    props.setFormDataApiLogs(nextFormData);
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    const payload = {
      Profile_Code: profileCode,
      pageNo: 1,
      recordPerPage: 5,
      sortColumn: props.orderBy,
      sortOrder: props.order,
      // searchStartDate: date1,
      // searchEndDate: date2,
      searchRequestedFor: requestedFor || "",
      searchApiType: apiType || "",
      searchModule: module || "",
      searchStartDate: date1 || "",
      searchEndDate: showDateRange ? date2 : null,
      searchStatus: searchStatus ? searchStatus.trim() : "",
      amountRange: false,
      dateRange: showDateRange,
    };
    props.getApiLogsData(payload);
    props.seacrhChipData(payload);
    setShowDateRangeError(false)
    props.close();
  };

  return (
    <MuiThemeProvider theme={customTheme}>
      <form onSubmit={e => onFormSubmit(e)}>
        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  focused: "focused",
                  input: classes.resize,
                },
              }}
              label="Requested For"
              value={requestedFor}
              onChange={handleInputChange}
              name="searchRequestedFor"
              id="Requested For"
              style={{ fontSize: "82%" }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  input: classes.resize,
                  focused: "focused",
                },
              }}
              placeholder="Select API Type"
              label="API Type"
              value={apiTypeValue || ""}
              onChange={onChange}
              name="searchApiType"
              id="searchApiType"
              select
              style={{ fontSize: "82%" }}
            >
              <MenuItem className={classes.dialogBoxDropDown} value="RTN API">
                RTN API
              </MenuItem>
              <MenuItem
                className={classes.dialogBoxDropDown}
                value="Send Fund API"
              >
                Send Fund API
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  input: classes.resize,
                  focused: "focused",
                },
              }}
              placeholder="Select Module"
              label="Module"
              value={moduleValue || ""}
              onChange={onChange}
              name="searchModule"
              id="searchModule"
              select
              style={{ fontSize: "82%" }}
            >
              <MenuItem
                className={classes.dialogBoxDropDown}
                value="Invite Page"
              >
                Invite Page
              </MenuItem>
              <MenuItem
                className={classes.dialogBoxDropDown}
                value="Merchant Submit Page"
              >
                Merchant Submit Page
              </MenuItem>
              <MenuItem
                className={classes.dialogBoxDropDown}
                value="Make Payment Page"
              >
                Make Payment Page
              </MenuItem>
              <MenuItem
                className={classes.dialogBoxDropDown}
                value="Merchant SignUp Page"
              >
                Merchant SignUp Page
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>
        
        <Grid container className={classes.margin}>
          <Grid item xs container alignItems="center">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <DatePicker
                autoOk
                variant="inline"
                label={showDateRange ? "From" : "Date"}
                value={date1 ? parseISO(date1) : null}
                onChange={handleSearchStartDateChange}
                format="yyyy/MM/dd"
                maxDate={new Date()}
                style={{
                  width: showDateRange ? "50%" : "100%",
                }}
              />

              {inputProps => (
                <TextField
                  {...inputProps}
                  className={classes.rootDialog}
                  InputLabelProps={{
                    classes: {
                      root: classes.activeInputLabelColor,
                      focused: "focused",
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: classes.activeInputColor,
                      focused: "focused",
                      input: classes.resize,
                    },
                  }}
                  label={showDateRange ? "From" : "Date"}
                  style={{
                    width: showDateRange ? "50%" : "100%",
                    fontSize: "100%",
                  }}
                />
              )}
            </MuiPickersUtilsProvider>
            {showDateRange && (
              <>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    autoOk
                    variant="inline"
                    label="To"
                    value={date2 ? parseISO(date2) : null}
                    onChange={handleSearchEndDateChange}
                    format="yyyy/MM/dd"
                    maxDate={new Date()}
                    style={{
                      width: "50%",
                    }}
                  />
                  {inputProps => (
                    <TextField
                      {...inputProps}
                      className={classes.rootDialog}
                      InputLabelProps={{
                        classes: {
                          root: classes.activeInputLabelColor,
                          focused: "focused",
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.activeInputColor,
                          focused: "focused",
                          input: classes.resize,
                        },
                      }}
                      label="To"
                      style={{ width: "50%", fontSize: "82%" }}
                    />
                  )}
                </MuiPickersUtilsProvider>
              </>
            )}
            {showDateRange && showDateRangeError && (
              <span style={{ fontSize: "12px", color: "primary", fontStyle: "italic" }}>
                {`Only ${getDateRange("RTP", "Months") ?? 2} months of date range allowed`}
              </span>
            )}
            <Checkbox
              checked={showDateRange}
              onChange={handleDateCheckboxChange}
              color="primary"
              style={{ marginLeft: "auto" }}
            />
            <label style={{ fontSize: "12px", marginBottom: 0 }}>
              Search In Range
            </label>
          </Grid>
        </Grid>
        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  input: classes.resize,
                  focused: "focused",
                },
              }}
              placeholder="Select Status"
              label="Status"
              value={searchStatus || ""}
              onChange={handleStatusChange}
              name="searchStatus"
              id="Current_Status"
              select
              style={{ fontSize: "82%" }}
            >
              <MenuItem className={classes.dialogBoxDropDown} value="Success">
                Success
              </MenuItem>
              <MenuItem className={classes.dialogBoxDropDown} value="Failure">
                Failure
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Grid
          container
          className={classes.margin}
          style={{ justifyContent: "end" }}
        >
          <Button
            variant="contained"
            className={classes.clearButton}
            disabled={
              !requestedFor &&
              !apiTypeValue &&
              !moduleValue &&
              !searchStatus &&
              !date1 &&
              !date2
            }
            onClick={() => handleClose()}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={`${classes.buttonStyle} ${searchButtonClasses.searchButton}`}
            disabled={
              !requestedFor &&
              !apiTypeValue &&
              !moduleValue &&
              !searchStatus &&
              !date1 &&
              !date2
            }
            type="submit"
          >
            Search
          </Button>
        </Grid>
      </form>
    </MuiThemeProvider>
  );
}

// Prop types for the component
ApiLogsSearch.propTypes = {
  close: PropTypes.func,
  seacrhChipData: PropTypes.func,
  getApiLogsData: PropTypes.func,
  activeSearchData: PropTypes.object,
};

// Mapping Redux state to props
const mapStateToProps = app => ({
  formData: app.merchantInformation,
  tableData: app.provideRegistration,
  userDetails: app.app,
  apiLogsData: app.merchantInformation,
});

// Connecting the component to Redux
export default connect(mapStateToProps, {
  setFormDataApiLogs,
  clearFormDataApiLogs,
  getApiLogsData,
})(ApiLogsSearch);
